// ./managers/PixiManager.ts
import {
  Container,
  Sprite,
  AnimatedSprite,
  Graphics,
  Texture,
  ColorSource,
  BLEND_MODES,
} from 'pixi.js';
import { AssetRegistry } from '@core/Assets/AssetRegistry';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import type { Spritesheet } from 'pixi.js';

// --- Interfaces for Props ---
export interface BasePixiProps {
  x?: number;
  y?: number;
  visible?: boolean;
  alpha?: number;
  rotation?: number;
  scale?: number | { x: number; y: number };
  tint?: ColorSource;
  zIndex?: number;
  parent?: Container;
  name?: string; // Maps to label
  blendMode?: BLEND_MODES;
  mask?: Container | null;
}

export interface SpriteProps extends BasePixiProps {
  anchor?: number | { x: number; y: number };
}

export interface AnimatedSpriteProps extends SpriteProps {
  autoPlay?: boolean;
  loop?: boolean;
  animationSpeed?: number;
  onComplete?: () => void;
  onLoop?: () => void;
  onFrameChange?: (currentFrame: number) => void;
}

// FIX: Changed from empty interface to type alias to satisfy ESLint
export type GraphicsProps = BasePixiProps;

export interface SpineProps extends BasePixiProps {
  skin?: string;
  animation?: string;
  loop?: boolean;
  timeScale?: number;
  autoUpdate?: boolean;
}

export class PixiManager {
  constructor(private assetRegistry: AssetRegistry) {}

  /**
   * Universal property applicator
   */
  private applyProperties<T extends Container>(target: T, props: any = {}): T {
    if (props.x !== undefined) target.x = props.x;
    if (props.y !== undefined) target.y = props.y;
    if (props.visible !== undefined) target.visible = props.visible;
    if (props.alpha !== undefined) target.alpha = props.alpha;
    if (props.rotation !== undefined) target.rotation = props.rotation;
    if (props.zIndex !== undefined) target.zIndex = props.zIndex;
    if (props.name !== undefined) target.label = props.name;

    if (props.blendMode !== undefined && 'blendMode' in target) {
      (target as any).blendMode = props.blendMode;
    }

    if (props.mask !== undefined) target.mask = props.mask;
    if (props.tint !== undefined && 'tint' in target)
      (target as any).tint = props.tint;

    // Handle Scale
    if (props.scale !== undefined) {
      if (typeof props.scale === 'number') {
        target.scale.set(props.scale);
      } else {
        target.scale.set(props.scale.x, props.scale.y);
      }
    }

    // Handle Anchor (Sprite/AnimatedSprite)
    if (props.anchor !== undefined && 'anchor' in target) {
      if (typeof props.anchor === 'number') {
        (target as any).anchor.set(props.anchor);
      } else {
        (target as any).anchor.set(props.anchor.x, props.anchor.y);
      }
    }

    // Handle Parent
    if (props.parent) {
      props.parent.addChild(target);
    }

    return target;
  }

  /**
   * Get a Sprite by Asset Key
   */
  public getSprite(key: string, props?: SpriteProps): Sprite {
    const texture = this.assetRegistry.getTexture(key);
    if (!texture) {
      console.warn(`[PixiManager] Texture not found for key: ${key}`);
      return this.applyProperties(new Sprite(Texture.EMPTY), props);
    }
    const sprite = new Sprite(texture);
    return this.applyProperties(sprite, props);
  }

  /**
   * Get a Sprite from a Spritesheet frame
   */
  public getAtlasSprite(
    atlas: Spritesheet,
    frameName: string,
    props?: {
      anchor?: number | { x: number; y: number };
      scale?: number;
    }
  ): Sprite | null {
    const texture = atlas.textures[frameName];

    if (!texture) {
      console.warn(`[PixiManager] Atlas frame "${frameName}" not found`);
      return null;
    }

    const sprite = new Sprite(texture);

    if (props?.anchor !== undefined) {
      if (typeof props.anchor === 'number') {
        sprite.anchor.set(props.anchor);
      } else {
        sprite.anchor.set(props.anchor.x, props.anchor.y);
      }
    }

    if (props?.scale !== undefined) {
      sprite.scale.set(props.scale);
    }

    return sprite;
  }

  /**
   * Get an AnimatedSprite from atlas animation
   */
  public getAtlasAnimation(
    atlas: Spritesheet,
    animationFrames: Texture[],
    props?: {
      autoPlay?: boolean;
      loop?: boolean;
      animationSpeed?: number;
      anchor?: number | { x: number; y: number };
    }
  ): AnimatedSprite {
    const anim = new AnimatedSprite(animationFrames);

    if (props?.animationSpeed !== undefined) {
      anim.animationSpeed = props.animationSpeed;
    }

    if (props?.loop !== undefined) {
      anim.loop = props.loop;
    }

    if (props?.anchor !== undefined) {
      if (typeof props.anchor === 'number') {
        anim.anchor.set(props.anchor);
      } else {
        anim.anchor.set(props.anchor.x, props.anchor.y);
      }
    }

    if (props?.autoPlay) {
      anim.play();
    }

    return anim;
  }

  /**
   * Get a Sprite from a raw Texture
   */
  public getSpriteFromTexture(texture: Texture, props?: SpriteProps): Sprite {
    const sprite = new Sprite(texture);
    return this.applyProperties(sprite, props);
  }

  /**
   * Get an AnimatedSprite from an array of Textures
   */
  public getAnimatedSprite(
    textures: Texture[],
    props?: AnimatedSpriteProps
  ): AnimatedSprite {
    const anim = new AnimatedSprite(textures);

    if (props?.animationSpeed !== undefined)
      anim.animationSpeed = props.animationSpeed;
    if (props?.loop !== undefined) anim.loop = props.loop;
    if (props?.onComplete) anim.onComplete = props.onComplete;
    if (props?.onLoop) anim.onLoop = props.onLoop;
    if (props?.onFrameChange) anim.onFrameChange = props.onFrameChange;

    this.applyProperties(anim, props);

    if (props?.autoPlay) {
      anim.play();
    }

    return anim;
  }

  /**
   * Get a Graphics object
   */
  public getGraphics(props?: GraphicsProps): Graphics {
    const g = new Graphics();
    return this.applyProperties(g, props);
  }

  /**
   * Get a Container
   */
  public getContainer(props?: BasePixiProps): Container {
    const c = new Container();
    return this.applyProperties(c, props);
  }

  /**
   * Get a Spine object (Async as it calls assetRegistry.loadSpine)
   */
  public async getSpine(
    key: string,
    props?: SpineProps
  ): Promise<Spine | null> {
    try {
      const spine = await this.assetRegistry.loadSpine(key);
      if (!spine) return null;

      this.applyProperties(spine, props);

      if (props?.skin) {
        spine.skeleton.setSkinByName(props.skin);
        spine.skeleton.setSlotsToSetupPose();
      }

      if (props?.autoUpdate !== undefined) spine.autoUpdate = props.autoUpdate;
      if (props?.timeScale !== undefined)
        spine.state.timeScale = props.timeScale;

      if (props?.animation) {
        spine.state.setAnimation(0, props.animation, props.loop ?? true);
      }

      return spine;
    } catch (e) {
      console.error(`[PixiManager] Failed to load spine: ${key}`, e);
      return null;
    }
  }
}
